/**
 * Variable Registry és Firebase integráció
 */

import { 
  collection, 
  doc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  query, 
  where, 
  orderBy, 
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { DocumentVariable } from '../types/documentVariables';
import { 
  ALL_VARIABLES, 
  VARIABLE_CATEGORIES, 
  VariableDefinition 
} from '../utils/variableRegistry';

const VARIABLES_COLLECTION = 'documentVariables';

export class VariableRegistryService {
  
  /**
   * Variable Registry változók szinkronizálása Firebase-szel
   */
  static async syncRegistryToFirebase(organizationId: string, userId: string): Promise<void> {
    console.log('🔄 Variable Registry szinkronizálás kezdődik...');
    
    // Meglévő változók lekérése
    const existingVariables = await this.getExistingVariables(organizationId);
    const existingKeys = new Set(existingVariables.map(v => v.key));
    
    // Registry változók deduplikálása kulcs alapján (biztonsági intézkedés)
    const uniqueRegistryVars = new Map<string, VariableDefinition>();
    ALL_VARIABLES.forEach(variable => {
      uniqueRegistryVars.set(variable.key, variable);
    });
    
    console.log(`📊 Statisztikák: ${existingVariables.length} meglévő, ${uniqueRegistryVars.size} registry változó`);
    
    const batch = writeBatch(db);
    let addedCount = 0;
    let updatedCount = 0;
    let skippedCount = 0;
    
    // Registry változók feldolgozása (deduplikált lista)
    for (const registryVar of Array.from(uniqueRegistryVars.values())) {
      const firebaseVar: Omit<DocumentVariable, 'id' | 'createdAt' | 'updatedAt' | 'createdBy'> = {
        name: registryVar.name,
        key: registryVar.key,
        type: registryVar.type,
        scope: 'global', // Registry változók alapértelmezetten globálisak
        organizationId,
        category: registryVar.category,
        order: this.getCategoryOrder(registryVar.category),
        isActive: true,
        required: registryVar.required || false,
        // Csak akkor adjuk hozzá, ha nem undefined
        ...(registryVar.description && { description: registryVar.description }),
        ...(registryVar.defaultValue && { defaultValue: registryVar.defaultValue })
      };
      
      if (existingKeys.has(registryVar.key)) {
        // Meglévő változó frissítése
        const existing = existingVariables.find(v => v.key === registryVar.key);
        if (existing) {
          // Ellenőrizzük, hogy szükséges-e a frissítés
          const needsUpdate = 
            existing.name !== registryVar.name ||
            existing.type !== registryVar.type ||
            existing.description !== registryVar.description ||
            existing.category !== registryVar.category;
            
          if (needsUpdate) {
            const docRef = doc(db, VARIABLES_COLLECTION, existing.id);
            batch.update(docRef, {
              ...firebaseVar,
              updatedAt: serverTimestamp()
            });
            updatedCount++;
            console.log(`🔄 Frissítés: ${registryVar.key} (${registryVar.name})`);
          } else {
            skippedCount++;
            console.log(`⏭️ Változatlan: ${registryVar.key} (${registryVar.name})`);
          }
        }
      } else {
        // Új változó hozzáadása
        const docRef = doc(collection(db, VARIABLES_COLLECTION));
        batch.set(docRef, {
          ...firebaseVar,
          id: docRef.id,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          createdBy: userId
        });
        addedCount++;
        console.log(`➕ Új változó: ${registryVar.key} (${registryVar.name})`);
      }
    }
    
    // Csak akkor commitoljuk, ha van változás
    if (addedCount > 0 || updatedCount > 0) {
      await batch.commit();
      console.log(`✅ Registry szinkronizálás kész: ${addedCount} új, ${updatedCount} frissített, ${skippedCount} változatlan változó`);
    } else {
      console.log(`✅ Minden változó naprakész: ${skippedCount} változó ellenőrizve, nincs szükség frissítésre`);
    }
  }
  
  /**
   * Meglévő változók lekérése
   */
  private static async getExistingVariables(organizationId: string): Promise<DocumentVariable[]> {
    const q = query(
      collection(db, VARIABLES_COLLECTION),
      where('organizationId', '==', organizationId),
      where('isActive', '==', true)
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as DocumentVariable));
  }
  
  /**
   * Kategória sorrend meghatározása
   */
  private static getCategoryOrder(category: string): number {
    const categoryIndex = VARIABLE_CATEGORIES.findIndex(c => c.id === category);
    return categoryIndex >= 0 ? (categoryIndex + 1) * 10 : 999;
  }
  
  /**
   * Registry változók ellenőrzése - vannak-e hiányzó változók?
   */
  static async checkMissingVariables(organizationId: string): Promise<{
    missing: VariableDefinition[];
    total: number;
    existing: number;
  }> {
    const existingVariables = await this.getExistingVariables(organizationId);
    const existingKeys = new Set(existingVariables.map(v => v.key));
    
    const missing = ALL_VARIABLES.filter(registryVar => 
      !existingKeys.has(registryVar.key)
    );
    
    return {
      missing,
      total: ALL_VARIABLES.length,
      existing: existingVariables.length
    };
  }
  
  /**
   * Változó definíció lekérése kulcs alapján
   */
  static getVariableDefinition(key: string): VariableDefinition | undefined {
    return ALL_VARIABLES.find(v => v.key === key);
  }
  
  /**
   * Elérhető változók kategóriánként
   */
  static getAvailableVariablesByCategory(): Record<string, VariableDefinition[]> {
    const grouped: Record<string, VariableDefinition[]> = {};
    
    VARIABLE_CATEGORIES.forEach(category => {
      grouped[category.id] = ALL_VARIABLES.filter(v => v.category === category.id);
    });
    
    return grouped;
  }
  
  /**
   * Duplikált változók eltávolítása (ugyanaz a kulcs többször)
   */
  static async removeDuplicateVariables(organizationId: string): Promise<number> {
    console.log('🧹 Duplikált változók keresése...');
    
    const existingVariables = await this.getExistingVariables(organizationId);
    const keyGroups = new Map<string, DocumentVariable[]>();
    
    // Csoportosítás kulcs szerint
    existingVariables.forEach(variable => {
      if (!keyGroups.has(variable.key)) {
        keyGroups.set(variable.key, []);
      }
      keyGroups.get(variable.key)!.push(variable);
    });
    
    // Duplikátumok keresése
    const duplicates: DocumentVariable[] = [];
    keyGroups.forEach((variables, key) => {
      if (variables.length > 1) {
        // Legújabb megtartása, többi törlése
        const sorted = variables.sort((a, b) => 
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
        );
        duplicates.push(...sorted.slice(1)); // Az első kivételével mind
        console.log(`🔍 Duplikátum találva: ${key} - ${variables.length} példány, ${sorted.length - 1} törlendő`);
      }
    });
    
    if (duplicates.length === 0) {
      console.log('✅ Nincsenek duplikált változók');
      return 0;
    }
    
    // Duplikátumok törlése
    const batch = writeBatch(db);
    duplicates.forEach(duplicate => {
      const docRef = doc(db, VARIABLES_COLLECTION, duplicate.id);
      batch.update(docRef, {
        isActive: false,
        updatedAt: serverTimestamp()
      });
    });
    
    await batch.commit();
    console.log(`🗑️ ${duplicates.length} duplikált változó deaktiválva`);
    
    return duplicates.length;
  }

  /**
   * Registry változók exportálása Firebase formátumba
   */
  static exportRegistryToFirebaseFormat(organizationId: string, userId: string): Omit<DocumentVariable, 'id' | 'createdAt' | 'updatedAt'>[] {
    return ALL_VARIABLES.map(registryVar => ({
      name: registryVar.name,
      key: registryVar.key,
      type: registryVar.type,
      scope: 'global' as const,
      required: registryVar.required || false,
      organizationId,
      category: registryVar.category,
      order: this.getCategoryOrder(registryVar.category),
      isActive: true,
      createdBy: userId,
      // templateId undefined marad (globális változók)
      // Csak akkor adjuk hozzá, ha nem undefined
      ...(registryVar.description && { description: registryVar.description }),
      ...(registryVar.defaultValue && { defaultValue: registryVar.defaultValue })
    }));
  }
}
